import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';

import { Colors, ModernPalette } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { AnimatedLoadingSwitch } from '@/components/settings/AnimatedLoadingSwitch';
import { NotificationPreferencesManager } from '@/lib/services/NotificationPreferencesManager';
import { summaryScheduler } from '@/lib/services/SummaryScheduler';
import type { NotificationPreferences } from '@/types/notifications';
import { getHeadingFont, getBodyFont } from '@/constants/typography';

/**
 * Notification Settings Screen
 *
 * Manages app-wide notification preferences:
 * - System permission check with prompt
 * - App-wide notification toggle (master switch)
 * - AI Summary notification toggles (Morning Brief, Evening Recap)
 */
export default function NotificationSettingsScreen() {
  const colorScheme = useColorScheme();
  const router = useRouter();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [systemPermissionGranted, setSystemPermissionGranted] = useState(true);
  const [canAskPermission, setCanAskPermission] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingToggle, setPendingToggle] = useState<
    null | 'appWide' | 'morningBrief' | 'eveningRecap'
  >(null);

  const palette = Colors[colorScheme ?? 'light'];
  const accentColor = palette.tint;
  const backgroundColor = palette.background;
  const textColor = palette.text;
  const textMutedColor = palette.textMuted;
  const borderMutedColor = palette.borderMuted;
  const warningColor = ModernPalette.warning;
  const sectionBackground = colorScheme === 'light' ? ModernPalette.white : palette.backgroundLight;

  const notificationsEnabled = preferences?.notificationsEnabled ?? true;
  const morningBriefEnabled = preferences?.morningBriefNotificationsEnabled ?? true;
  const eveningRecapEnabled = preferences?.eveningRecapNotificationsEnabled ?? true;

  const loadPreferences = useCallback(async () => {
    const manager = NotificationPreferencesManager.getInstance();
    await manager.initialize();

    const prefs = manager.getPreferences();
    setPreferences(prefs);

    const { granted, canAskAgain } = await manager.checkSystemPermissions();
    setSystemPermissionGranted(granted);
    setCanAskPermission(canAskAgain);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  // Reload on screen focus to catch permission changes from system settings
  useFocusEffect(
    useCallback(() => {
      loadPreferences();
    }, [loadPreferences])
  );

  const updatePreference = useCallback(
    async (
      updates: Partial<NotificationPreferences>,
      key: 'appWide' | 'morningBrief' | 'eveningRecap'
    ) => {
      setPendingToggle(key);
      try {
        const manager = NotificationPreferencesManager.getInstance();
        const updated = await manager.updatePreferences(updates);
        setPreferences(updated);
        await summaryScheduler.resyncScheduledNotifications();
      } catch (err) {
        console.error('[notification-settings] Failed to update preferences:', err);
        Alert.alert(
          'Notification Settings',
          'Something went wrong while saving your changes. Please try again.'
        );
        await loadPreferences();
      } finally {
        setPendingToggle(null);
      }
    },
    [loadPreferences]
  );

  const handleToggleAppWide = useCallback(
    (value: boolean) => {
      updatePreference({ notificationsEnabled: value }, 'appWide');
    },
    [updatePreference]
  );

  const handleToggleMorningBrief = useCallback(
    (value: boolean) => {
      updatePreference({ morningBriefNotificationsEnabled: value }, 'morningBrief');
    },
    [updatePreference]
  );

  const handleToggleEveningRecap = useCallback(
    (value: boolean) => {
      updatePreference({ eveningRecapNotificationsEnabled: value }, 'eveningRecap');
    },
    [updatePreference]
  );

  const handleRequestPermission = useCallback(async () => {
    const manager = NotificationPreferencesManager.getInstance();
    const granted = await manager.requestPermissions();

    if (granted) {
      setSystemPermissionGranted(true);
      Alert.alert('Notifications Enabled', 'You can now receive notifications from FimFam.');
    } else {
      setCanAskPermission(false);
      Alert.alert(
        'Permission Denied',
        'To receive notifications, please enable them in your device settings.'
      );
    }
  }, []);

  const handleOpenSystemSettings = useCallback(async () => {
    const manager = NotificationPreferencesManager.getInstance();
    await manager.openSystemSettings();
  }, []);

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor }]} edges={['top']}>
      <View style={[styles.header, { borderBottomColor: borderMutedColor }]}>
        <TouchableOpacity onPress={handleBack} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={accentColor} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: textColor }]}>Notifications</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView style={styles.scrollView}>
        {!systemPermissionGranted && (
          <View style={[styles.permissionBanner, { backgroundColor: warningColor + '20' }]}>
            <View style={styles.permissionBannerContent}>
              <Ionicons name="warning" size={20} color={warningColor} />
              <View style={styles.permissionBannerText}>
                <Text style={[styles.permissionTitle, { color: textColor }]}>
                  Notifications Disabled
                </Text>
                <Text style={[styles.permissionDescription, { color: textMutedColor }]}>
                  Enable notifications in your device settings to receive alerts.
                </Text>
              </View>
            </View>
            <TouchableOpacity
              style={[styles.permissionButton, { backgroundColor: warningColor }]}
              onPress={canAskPermission ? handleRequestPermission : handleOpenSystemSettings}>
              <Text style={styles.permissionButtonText}>
                {canAskPermission ? 'Enable' : 'Open Settings'}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        <View style={[styles.section, { backgroundColor: sectionBackground }]}>
          <View style={styles.preferenceRow}>
            <View style={styles.preferenceInfo}>
              <Text style={[styles.preferenceTitle, { color: textColor }]}>
                App Notifications
              </Text>
              <Text style={[styles.preferenceDescription, { color: textMutedColor }]}>
                Master switch for all FimFam notifications.
              </Text>
            </View>
            {pendingToggle === 'appWide' ? (
              <ActivityIndicator size="small" color={accentColor} />
            ) : (
              <AnimatedLoadingSwitch
                isLoading={isLoading}
                targetValue={notificationsEnabled}
                onValueChange={handleToggleAppWide}
                trackColor={{ true: accentColor, false: ModernPalette.borderLight }}
                thumbColor={ModernPalette.white}
                disabled={pendingToggle !== null}
              />
            )}
          </View>
        </View>

        <View style={[styles.section, styles.sectionWithHeader]}>
          <Text
            style={[
              styles.sectionHeader,
              { color: textMutedColor },
              !notificationsEnabled && styles.sectionContentDisabled,
            ]}
          >
            AI SUMMARY NOTIFICATIONS
          </Text>
          <View
            style={[
              styles.sectionContent,
              { backgroundColor: sectionBackground },
              !notificationsEnabled && styles.sectionContentDisabled,
            ]}
            pointerEvents={notificationsEnabled ? 'auto' : 'none'}
          >
            <View style={styles.preferenceRow}>
              <View style={styles.preferenceInfo}>
                <Text style={[styles.preferenceTitle, { color: textColor }]}>Morning Brief</Text>
                <Text style={[styles.preferenceDescription, { color: textMutedColor }]}>
                  Receive daily morning summary notifications.
                </Text>
              </View>
              {pendingToggle === 'morningBrief' ? (
                <ActivityIndicator size="small" color={accentColor} />
              ) : (
                <AnimatedLoadingSwitch
                  isLoading={isLoading}
                  targetValue={morningBriefEnabled}
                  onValueChange={handleToggleMorningBrief}
                  trackColor={{ true: accentColor, false: ModernPalette.borderLight }}
                  thumbColor={ModernPalette.white}
                  disabled={!notificationsEnabled || pendingToggle !== null}
                />
              )}
            </View>

            <View style={[styles.preferenceDivider, { backgroundColor: borderMutedColor }]} />

            <View style={styles.preferenceRow}>
              <View style={styles.preferenceInfo}>
                <Text style={[styles.preferenceTitle, { color: textColor }]}>Evening Recap</Text>
                <Text style={[styles.preferenceDescription, { color: textMutedColor }]}>
                  Receive daily evening summary notifications.
                </Text>
              </View>
              {pendingToggle === 'eveningRecap' ? (
                <ActivityIndicator size="small" color={accentColor} />
              ) : (
                <AnimatedLoadingSwitch
                  isLoading={isLoading}
                  targetValue={eveningRecapEnabled}
                  onValueChange={handleToggleEveningRecap}
                  trackColor={{ true: accentColor, false: ModernPalette.borderLight }}
                  thumbColor={ModernPalette.white}
                  disabled={!notificationsEnabled || pendingToggle !== null}
                />
              )}
            </View>
          </View>

          {!notificationsEnabled && (
            <View style={[styles.disabledNotice, { backgroundColor: sectionBackground }]}>
              <Ionicons name="information-circle" size={16} color={textMutedColor} />
              <Text style={[styles.disabledNoticeText, { color: textMutedColor }]}>
                Enable app notifications above to receive AI summaries
              </Text>
            </View>
          )}
        </View>

        <View style={styles.footer} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
    fontFamily: getHeadingFont(),
  },
  headerSpacer: {
    width: 32,
  },
  scrollView: {
    flex: 1,
  },
  permissionBanner: {
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 12,
    padding: 16,
  },
  permissionBannerContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  permissionBannerText: {
    flex: 1,
  },
  permissionTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
    fontFamily: getBodyFont(),
  },
  permissionDescription: {
    fontSize: 12,
    fontFamily: getBodyFont(),
  },
  permissionButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  permissionButtonText: {
    color: ModernPalette.textInverse,
    fontSize: 14,
    fontWeight: '600',
    fontFamily: getBodyFont(),
  },
  section: {
    marginTop: 20,
    borderRadius: 12,
    marginHorizontal: 16,
    overflow: 'hidden',
  },
  sectionWithHeader: {
    backgroundColor: 'transparent',
    borderRadius: 0,
  },
  sectionHeader: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
    marginLeft: 16,
    fontFamily: getHeadingFont(),
  },
  sectionContent: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  sectionContentDisabled: {
    opacity: 0.45,
  },
  preferenceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  preferenceInfo: {
    flex: 1,
    marginRight: 12,
  },
  preferenceTitle: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: getBodyFont(),
  },
  preferenceDescription: {
    fontSize: 12,
    marginTop: 4,
    fontFamily: getBodyFont(),
  },
  preferenceDivider: {
    height: StyleSheet.hairlineWidth,
    marginLeft: 16,
    marginRight: 16,
  },
  disabledNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  disabledNoticeText: {
    fontSize: 10,
    fontStyle: 'italic',
    fontFamily: getBodyFont(),
  },
  footer: {
    height: 40,
  },
});
